import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import PageContainer from "../../components/layout/PageContainer";
import ServiceBriefingPageShell from "../../components/service-briefings/ServiceBriefingPageShell";
import ServiceBriefingLinesTable, {
  LineDraft,
  emptyLine,
  isLineValid,
  parseAmount,
} from "../../components/service-briefings/ServiceBriefingLinesTable";
import {
  SERVICE_BRIEFING_KINDS,
  SERVICE_BRIEFING_STATUSES,
  euro,
  formatDay,
  kindLabel,
  previewName,
  statusLabel,
  todayInMadrid,
} from "../../components/service-briefings/serviceBriefingLabels";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { useGetCompaniesQuery } from "../../store/apis/companyApi";
import { useGetUsersQuery } from "../../store/apis/usersApi";
import {
  useCreateServiceBriefingMutation,
  useGetServiceBriefingQuery,
  useListServiceBriefingOcOptionsQuery,
  useUpdateServiceBriefingMutation,
} from "../../store/apis/serviceBriefingApi";
import {
  ServiceBriefingKind,
  ServiceBriefingOcOption,
  ServiceBriefingStatus,
} from "../../types/serviceBriefing";

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

interface Option {
  id: string;
  label: string;
}

/** La OC elegida. En edición solo se sabe lo que trae la ficha, no sus líneas. */
type OcValue = Pick<
  ServiceBriefingOcOption,
  "id" | "ocNumber" | "campaign" | "clientName" | "companyId"
>;

/**
 * Alta y edición de un briefing de House of Stories, House of AI o House of Digital.
 *
 * El nombre no se pide: lo compone el servidor con mes, año, tipo, marca y campaña. Aquí
 * solo se previsualiza. Los assets no van en este formulario: se suben desde la ficha,
 * porque la ruta de Storage lleva el id del briefing y en el alta todavía no existe.
 */
const ServiceBriefingForm: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();

  const initialKind = searchParams.get("kind") as ServiceBriefingKind | null;
  const now = new Date();

  const [kind, setKind] = useState<ServiceBriefingKind>(
    initialKind && SERVICE_BRIEFING_KINDS.includes(initialKind)
      ? initialKind
      : "HOUSE_OF_STORIES"
  );
  const [companyId, setCompanyId] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [brandOrProduct, setBrandOrProduct] = useState("");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [budget, setBudget] = useState("");
  const [requestDate, setRequestDate] = useState(todayInMadrid());
  const [deliveryDate, setDeliveryDate] = useState("");
  const [assigneeUserId, setAssigneeUserId] = useState("");
  const [status, setStatus] = useState<ServiceBriefingStatus>("DRAFT");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [oc, setOc] = useState<OcValue | null>(null);

  // Buscador de OC: en el servidor, con debounce.
  const [ocQuery, setOcQuery] = useState("");
  const [debouncedOcQuery, setDebouncedOcQuery] = useState("");
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedOcQuery(ocQuery.trim()), 300);
    return () => clearTimeout(handle);
  }, [ocQuery]);
  const { data: ocOptionsData, isFetching: loadingOcOptions } =
    useListServiceBriefingOcOptionsQuery({
      search: debouncedOcQuery || undefined,
    });
  const ocOptions = ocOptionsData?.data ?? [];

  /**
   * Rellena el briefing con la OC: la casa sale de la emisora, y cliente, campaña, fechas
   * y conceptos con su precio, de la OC. El coste se deja en blanco, que es lo único que
   * hay que poner. La entrega solo se pisa si la OC trae fechas.
   */
  const applyOc = (option: ServiceBriefingOcOption) => {
    setOc(option);
    setKind(option.kind);
    if (option.companyId) setCompanyId(option.companyId);
    setCampaignName(option.campaign);
    setRequestDate(option.ocDate);
    const [ocYear, ocMonth] = option.ocDate.split("-").map(Number);
    setYear(ocYear);
    setMonth(ocMonth);
    if (option.deliveryDate) setDeliveryDate(option.deliveryDate);
    setLines(
      option.lines.map((l) => ({
        concept: l.concept,
        quantity: String(l.quantity),
        unitCost: "",
        unitPrice: String(l.unitPrice),
      }))
    );
  };

  // Con OC, la casa no se cambia; el cliente tampoco si la OC lo trae (en edición no se
  // sabe, así que también se bloquea: el backend ya validó que casaba al guardar).
  const kindLocked = Boolean(oc);
  const clientLocked = Boolean(oc) && oc?.companyId !== null;

  const { data: existingData, isFetching: loadingExisting } =
    useGetServiceBriefingQuery(id ?? "", { skip: !id });
  const existing = existingData?.data ?? null;

  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!existing || loaded) return;
    setKind(existing.kind);
    setCompanyId(existing.companyId);
    setCampaignName(existing.campaignName);
    setBrandOrProduct(existing.brandOrProduct ?? "");
    setMonth(existing.month);
    setYear(existing.year);
    setBudget(existing.budget ? String(existing.budget) : "");
    setRequestDate(existing.requestDate);
    setDeliveryDate(existing.deliveryDate ?? "");
    setAssigneeUserId(existing.assignee?.id ?? "");
    setStatus(existing.status);
    setNotes(existing.notes ?? "");
    setOc(
      existing.oc
        ? {
            id: existing.oc.id,
            ocNumber: existing.oc.ocNumber,
            campaign: existing.oc.campaign,
            clientName: existing.companyName,
            companyId: existing.companyId,
          }
        : null
    );
    setLines(
      existing.lines.map((l) => ({
        concept: l.concept,
        quantity: String(l.quantity),
        unitCost: String(l.unitCost),
        unitPrice: String(l.unitPrice),
      }))
    );
    setLoaded(true);
  }, [existing, loaded]);

  const { data: companiesData, isLoading: loadingCompanies } =
    useGetCompaniesQuery();
  const companyOptions = useMemo<Option[]>(
    () =>
      (companiesData?.data ?? [])
        .map((c) => ({
          id: c.id,
          label: c.nameCommercial || c.nameFiscal || c.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "es")),
    [companiesData]
  );

  const { data: usersData } = useGetUsersQuery();
  const userOptions = useMemo<Option[]>(
    () =>
      (usersData?.data ?? [])
        .filter((u) => u.active !== false && u.role !== "CLIENT")
        .map((u) => ({
          id: u.id,
          label:
            [u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
            u.email,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "es")),
    [usersData]
  );

  const [createBriefing, { isLoading: isCreating }] =
    useCreateServiceBriefingMutation();
  const [updateBriefing, { isLoading: isUpdating }] =
    useUpdateServiceBriefingMutation();
  const isSaving = isCreating || isUpdating;

  const datesValid =
    requestDate !== "" && (deliveryDate === "" || deliveryDate >= requestDate);

  // Lo que falta, para que un botón deshabilitado diga por qué.
  const missing: string[] = [
    !companyId && t("serviceBriefings.fields.client", "Cliente"),
    !campaignName.trim() &&
      t("serviceBriefings.fields.campaignName", "Nombre de campaña"),
    !datesValid &&
      t(
        "serviceBriefings.missing.dates",
        "Fechas (la entrega no puede ser anterior a la solicitud)"
      ),
    !lines.every(isLineValid) &&
      t(
        "serviceBriefings.missing.lines",
        "Líneas completas (concepto, coste y precio)"
      ),
  ].filter((entry): entry is string => Boolean(entry));

  const canSubmit = missing.length === 0 && !isSaving;

  const handleSubmit = async () => {
    const payload = {
      kind,
      companyId,
      campaignName: campaignName.trim(),
      brandOrProduct: brandOrProduct.trim() || null,
      month,
      year,
      budget: parseAmount(budget),
      notes: notes.trim() || null,
      requestDate,
      deliveryDate: deliveryDate || null,
      assigneeUserId: assigneeUserId || null,
      status,
      ocOrderId: oc?.id ?? null,
      lines: lines.map((l) => ({
        concept: l.concept.trim(),
        quantity: parseAmount(l.quantity),
        unitCost: parseAmount(l.unitCost),
        unitPrice: parseAmount(l.unitPrice),
      })),
    };

    try {
      const response =
        isEdit && id
          ? await updateBriefing({ id, body: payload }).unwrap()
          : await createBriefing(payload).unwrap();
      const saved = response.data;
      dispatch(
        setToastMessage({
          message: isEdit
            ? t("serviceBriefings.form.updated", "Briefing guardado")
            : t(
                "serviceBriefings.form.created",
                "Briefing creado. Ya puedes añadir los assets."
              ),
          severity: "success",
        })
      );
      navigate(saved ? `/service-briefings/${saved.id}` : "/service-briefings");
    } catch (error: unknown) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ??
        t("serviceBriefings.form.failed", "No se pudo guardar el briefing");
      dispatch(setToastMessage({ message, severity: "error" }));
    }
  };

  const section = (title: string, children: React.ReactNode) => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey[200]}`,
        p: { xs: 2, md: 3 },
      }}
    >
      <Typography variant="subtitle1" mb={2}>
        {title}
      </Typography>
      {children}
    </Paper>
  );

  const name = previewName(kind, month, year, brandOrProduct, campaignName);

  return (
    <ServiceBriefingPageShell>
      <PageContainer sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box>
          <Typography variant="h5">
            {isEdit
              ? t("serviceBriefings.form.editTitle", "Editar briefing")
              : t("serviceBriefings.form.newTitle", "Nuevo briefing")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("serviceBriefings.form.name", "Nombre")}:{" "}
            <strong>{campaignName.trim() ? name : "—"}</strong>
          </Typography>
        </Box>

        {loadingExisting && (
          <Box textAlign="center" py={2}>
            <CircularProgress size={22} />
          </Box>
        )}

        {section(
          t("serviceBriefings.form.ocSection", "OC"),
          <Stack gap={1}>
            <Autocomplete<OcValue>
              size="small"
              options={ocOptions}
              value={oc}
              loading={loadingOcOptions}
              filterOptions={(options) => options}
              onInputChange={(_, value, reason) => {
                if (reason === "input") setOcQuery(value);
              }}
              onChange={(_, option) => {
                if (!option) {
                  setOc(null);
                  return;
                }
                const full = ocOptions.find((o) => o.id === option.id);
                if (full) applyOc(full);
              }}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              getOptionLabel={(option) =>
                [option.ocNumber, option.clientName, option.campaign]
                  .filter(Boolean)
                  .join(" · ")
              }
              renderOption={(props, option) => {
                const full = option as ServiceBriefingOcOption;
                return (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">
                        {option.ocNumber} · {option.clientName || "—"} ·{" "}
                        {option.campaign}
                      </Typography>
                      {full.kind && (
                        <Typography variant="caption" color="text.secondary">
                          {kindLabel(t, full.kind)} · {formatDay(full.ocDate)} ·{" "}
                          {euro.format(full.totalPrice)}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              noOptionsText={t(
                "serviceBriefings.form.ocNone",
                "No hay OC de House of Stories, AI o Digital que casen"
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("serviceBriefings.form.ocPick", "Sale de la OC")}
                  placeholder={t(
                    "serviceBriefings.form.ocPlaceholder",
                    "Número, cliente o campaña"
                  )}
                />
              )}
            />
            <Typography variant="caption" color="text.secondary">
              {oc
                ? t(
                    "serviceBriefings.form.ocHelpLinked",
                    "Casa, cliente, fechas y conceptos vienen de la OC: solo hay que poner el coste de cada línea."
                  )
                : t(
                    "serviceBriefings.form.ocHelp",
                    "Opcional. Si eliges una OC se rellenan la casa, el cliente, las fechas y los conceptos con su precio."
                  )}
            </Typography>
          </Stack>
        )}

        {section(
          t("serviceBriefings.form.general", "Datos generales"),
          <Stack gap={2}>
            <Stack direction={{ xs: "column", md: "row" }} gap={2}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>
                  {t("serviceBriefings.fields.kind", "Tipo")}
                </InputLabel>
                <Select
                  value={kind}
                  disabled={kindLocked}
                  label={t("serviceBriefings.fields.kind", "Tipo")}
                  onChange={(e) =>
                    setKind(e.target.value as ServiceBriefingKind)
                  }
                >
                  {SERVICE_BRIEFING_KINDS.map((k) => (
                    <MenuItem key={k} value={k}>
                      {kindLabel(t, k)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Autocomplete
                size="small"
                sx={{ flex: 2 }}
                options={companyOptions}
                disabled={clientLocked}
                loading={loadingCompanies}
                value={companyOptions.find((o) => o.id === companyId) ?? null}
                onChange={(_, option) => setCompanyId(option?.id ?? "")}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label={t("serviceBriefings.fields.client", "Cliente")}
                  />
                )}
              />
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} gap={2}>
              <TextField
                size="small"
                required
                sx={{ flex: 1 }}
                label={t(
                  "serviceBriefings.fields.campaignName",
                  "Nombre de campaña"
                )}
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
              />
              <TextField
                size="small"
                sx={{ flex: 1 }}
                label={t(
                  "serviceBriefings.fields.brandOrProduct",
                  "Marca o producto"
                )}
                value={brandOrProduct}
                onChange={(e) => setBrandOrProduct(e.target.value)}
              />
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} gap={2}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>
                  {t("serviceBriefings.fields.month", "Mes")}
                </InputLabel>
                <Select
                  value={month}
                  label={t("serviceBriefings.fields.month", "Mes")}
                  onChange={(e) => setMonth(Number(e.target.value))}
                >
                  {MONTHS.map((m) => (
                    <MenuItem key={m} value={m}>
                      {String(m).padStart(2, "0")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="number"
                sx={{ flex: 1 }}
                label={t("serviceBriefings.fields.year", "Año")}
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              />
              <TextField
                size="small"
                sx={{ flex: 1 }}
                label={t("serviceBriefings.fields.budget", "Presupuesto (€)")}
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                inputProps={{ inputMode: "decimal" }}
              />
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} gap={2}>
              <TextField
                size="small"
                type="date"
                required
                sx={{ flex: 1 }}
                label={t(
                  "serviceBriefings.fields.requestDate",
                  "Fecha de solicitud"
                )}
                value={requestDate}
                onChange={(e) => setRequestDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                type="date"
                sx={{ flex: 1 }}
                label={t(
                  "serviceBriefings.fields.deliveryDate",
                  "Fecha de entrega"
                )}
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: requestDate || undefined }}
                error={!datesValid && deliveryDate !== ""}
              />
              <Autocomplete
                size="small"
                sx={{ flex: 1 }}
                options={userOptions}
                value={userOptions.find((o) => o.id === assigneeUserId) ?? null}
                onChange={(_, option) => setAssigneeUserId(option?.id ?? "")}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("serviceBriefings.fields.assignee", "Asignado a")}
                  />
                )}
              />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>
                  {t("serviceBriefings.fields.status", "Estado")}
                </InputLabel>
                <Select
                  value={status}
                  label={t("serviceBriefings.fields.status", "Estado")}
                  onChange={(e) =>
                    setStatus(e.target.value as ServiceBriefingStatus)
                  }
                >
                  {SERVICE_BRIEFING_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {statusLabel(t, s)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <TextField
              size="small"
              multiline
              minRows={3}
              label={t("serviceBriefings.fields.notes", "Notas")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Stack>
        )}

        {section(
          t("serviceBriefings.form.lines", "Líneas"),
          <ServiceBriefingLinesTable
            lines={lines}
            onChange={setLines}
            fromOc={Boolean(oc)}
          />
        )}

        {!isEdit && (
          <Alert severity="info">
            {t(
              "serviceBriefings.form.assetsAfterCreate",
              "Los assets se añaden desde la ficha, en cuanto guardes el briefing."
            )}
          </Alert>
        )}

        {missing.length > 0 && (
          <Alert severity="warning">
            {t("serviceBriefings.form.missing", "Falta:")} {missing.join(", ")}
          </Alert>
        )}

        <Stack direction="row" gap={1} justifyContent="flex-end">
          <Button onClick={() => navigate(-1)}>
            {t("serviceBriefings.form.cancel", "Cancelar")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isSaving ? (
              <CircularProgress size={20} />
            ) : (
              t("serviceBriefings.form.save", "Guardar")
            )}
          </Button>
        </Stack>
      </PageContainer>
    </ServiceBriefingPageShell>
  );
};

export default ServiceBriefingForm;
